import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, Mail, Phone } from 'lucide-react';
import { fetchSuppliers } from '../services/api';

const Suppliers = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    filterSuppliers();
  }, [suppliers, searchTerm]);

  const loadSuppliers = async () => {
    try {
      const data = await fetchSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSuppliers = () => {
    if (!searchTerm) {
      setFilteredSuppliers(suppliers);
      return;
    }

    const filtered = suppliers.filter(supplier =>
      supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredSuppliers(filtered);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading suppliers...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Suppliers</h1>
        <button onClick={() => navigate('/suppliers/new')} className="btn-primary flex items-center gap-2">
          <Plus size={20} />
          Add Supplier
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search suppliers..."
            className="input-field pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSuppliers.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No suppliers found. Click "Add Supplier" to add your first supplier.
          </div>
        ) : (
          filteredSuppliers.map((supplier) => (
            <div key={supplier.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-primary-100 rounded-lg p-3">
                  <Building2 className="text-primary-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{supplier.name}</h3>
                  <p className="text-sm text-gray-500">{supplier.category}</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm">
                  <span className="text-gray-600">Contact:</span> {supplier.contact_person}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail size={14} />
                  <span>{supplier.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone size={14} />
                  <span>{supplier.phone}</span>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Lead Time:</span>
                  <span>{supplier.lead_time_days} days</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Min Order:</span>
                  <span>${Number(supplier.min_order_value || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button onClick={() => navigate(`/suppliers/${supplier.id}/edit`)} className="flex-1 btn-secondary text-sm">Edit</button>
                <button onClick={() => navigate(`/suppliers/${supplier.id}`)} className="flex-1 btn-primary text-sm">View Details</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Suppliers;
